import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { CONTACT, TRADE_CATEGORIES } from "@/data/products";
import { scrollToSection } from "@/lib/smooth";
import { Reveal } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

const CategoryPage = () => {
  const { categoryId } = useParams();
  const category = TRADE_CATEGORIES.find((c) => c.id === categoryId);

  useEffect(() => {
    scrollToSection(0);
  }, [categoryId]);

  if (!category) return <Navigate to="/" replace />;

  const waUrl = `${CONTACT.whatsapp}${encodeURIComponent(` — Category: ${category.name}`)}`;

  return (
    <div data-testid={`category-page-${category.id}`} className="min-h-screen bg-parchment">
      <header className="flex items-center justify-between px-6 py-5 sm:px-12 lg:px-20">
        <Link to="/" data-testid="category-logo-link" aria-label="Praveen Textiles — home">
          <img src="/logo-full.png" alt="Praveen Textiles" className="h-12 w-auto sm:h-14" />
        </Link>
        <Link
          to="/#products"
          data-testid="back-to-categories"
          className="group flex items-center gap-2 font-body text-xs font-semibold tracking-wide text-ink/70 transition-colors hover:text-crimson"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
          All Categories
        </Link>
      </header>

      <main className="px-6 pb-24 pt-10 sm:px-12 sm:pt-14 lg:px-20">
        <div className="mx-auto max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex items-center gap-4 font-accent text-[11px] font-semibold tracking-[0.35em] text-crimson"
          >
            <span className="h-px w-12 bg-crimson" /> PRODUCT RANGE · {category.n}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.85, ease: EASE }}
            className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {category.name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-clay"
          >
            {category.items.length} lines stocked · current rates on request
          </motion.p>

          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {category.items.map((it, i) => (
              <motion.div
                key={it}
                data-testid={`category-item-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.04, duration: 0.55, ease: EASE }}
                className="border border-ink/10 bg-white/60 px-5 py-4 font-body text-sm font-medium text-ink transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-crimson/50"
              >
                {it}
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-14 flex flex-wrap items-center gap-4 border-t border-ink/10 pt-10">
            <a
              data-testid="category-whatsapp-btn"
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-full bg-[#1FA855] px-7 py-4 font-body text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#25D366]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Ask Current Rates on WhatsApp
            </a>
            <Link
              to="/#contact"
              data-testid="category-enquiry-link"
              className="group flex items-center gap-2 rounded-full border border-ink/25 px-7 py-4 font-body text-sm font-semibold text-ink transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-gold/10"
            >
              Send an Enquiry
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </main>
    </div>
  );
};

export default CategoryPage;
